import { useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  CardTokenizationError,
  getPaymentClientConfig,
  loadSquareSdk,
  type SquareCard,
  type SquareTokenResult,
} from '@/api/payments';

export type TokenizeFn = () => Promise<string>;

interface CardPaymentFieldProps {
  tenantSlug: string;
  locationRef: string;
  onReady: (tokenize: TokenizeFn) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
}

type FieldStatus = 'loading' | 'ready' | 'unconfigured' | 'unsupported' | 'error';

function CardPaymentField({
  tenantSlug,
  locationRef,
  onReady,
  onError,
  disabled = false,
}: CardPaymentFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onReadyRef = useRef(onReady);
  const onErrorRef = useRef(onError);
  const [status, setStatus] = useState<FieldStatus>('loading');

  useEffect(() => {
    onReadyRef.current = onReady;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    let cancelled = false;
    let card: SquareCard | null = null;

    async function initCard() {
      setStatus('loading');
      try {
        const config = await getPaymentClientConfig(tenantSlug, locationRef);
        if (cancelled) return;
        if (!config.configured) {
          setStatus('unconfigured');
          return;
        }
        if (config.provider !== 'square') {
          setStatus('unsupported');
          return;
        }

        const applicationId = config.clientConfig['applicationId'];
        const squareLocationId = config.clientConfig['squareLocationId'];
        if (!applicationId || !squareLocationId) {
          throw new Error('SQUARE_CLIENT_CONFIG_INCOMPLETE');
        }

        const sdk = await loadSquareSdk(config.environment);
        if (cancelled) return;
        const payments = sdk.payments(applicationId, squareLocationId);
        card = await payments.card();
        if (cancelled || !containerRef.current) {
          await card.destroy?.();
          return;
        }

        await card.attach(containerRef.current);
        if (cancelled) {
          await card.destroy?.();
          return;
        }

        const mountedCard = card;
        setStatus('ready');
        onReadyRef.current(async () => tokenizeCard(mountedCard));
      } catch (error) {
        if (cancelled) return;
        setStatus('error');
        onErrorRef.current?.(error instanceof Error ? error : new Error('CARD_FIELD_INIT_FAILED'));
      }
    }

    void initCard();

    return () => {
      cancelled = true;
      void card?.destroy?.();
    };
  }, [tenantSlug, locationRef]);

  return (
    <Stack spacing={1}>
      {status === 'loading' && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2">Loading secure card field...</Typography>
        </Stack>
      )}

      {status === 'unconfigured' && (
        <Alert severity="info">Card payments are not configured for this location.</Alert>
      )}

      {status === 'unsupported' && (
        <Alert severity="info">This payment provider is not supported for card entry.</Alert>
      )}

      {status === 'error' && (
        <Alert severity="error">The secure card field could not be loaded.</Alert>
      )}

      <Box
        ref={containerRef}
        aria-disabled={disabled || undefined}
        data-testid="square-card-container"
        sx={{
          display: status === 'loading' || status === 'ready' ? 'block' : 'none',
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          minHeight: status === 'ready' ? 46 : 0,
          border: status === 'ready' ? '1px solid' : 'none',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          p: status === 'ready' ? 1.25 : 0,
        }}
      />
    </Stack>
  );
}

async function tokenizeCard(card: SquareCard): Promise<string> {
  const result = await card.tokenize();
  if (result.status === 'OK' && result.token) return result.token;
  throw new CardTokenizationError(tokenizationDetail(result), {
    validation: isValidationTokenizationResult(result),
  });
}

function tokenizationDetail(result: SquareTokenResult): string {
  return result.errors?.[0]?.message ?? result.errors?.[0]?.detail ?? result.status;
}

function isValidationTokenizationResult(result: SquareTokenResult): boolean {
  if (result.status.toUpperCase() === 'INVALID') return true;
  return (
    result.errors?.some((error) => {
      if (error.field) return true;
      const code = `${error.code ?? ''} ${error.category ?? ''}`.toUpperCase();
      return /CARD|CVV|CVC|EXPIR|POSTAL|VALID/.test(code);
    }) ?? false
  );
}

export default CardPaymentField;
